import { RadioGroup } from '@/components/RadioGroup';
import {
    EFrequency,
    selectBillsPaidFrequency,
    setBillsPaidFrequency,
} from '@/context/borrower';
import { useAppDispatch, useAppSelector } from '@/context/storeHooks';
import { useFieldCopy } from '@/copy';

export interface IHousingFrequency {
    baseId: string;
    showErrors: boolean;
}

export const testId = 'HousingFrequency';

/**
 * How often the applicant pays their mortgage or rent.
 *
 * Written to billsPaidFrequency, the field the serviceability call already
 * converts expenses with. On a journey that skips the expenses screen,
 * mortgage or rent is the only expense collected, so that field governs this
 * answer alone; the expenses screen keeps asking its own broader question.
 *
 * Asked rather than derived from the pay cycle: someone paid weekly may still
 * pay rent fortnightly or a mortgage monthly, and deriving it meant the label
 * claimed one period while the calculation used another.
 */
export function HousingFrequency({
    baseId,
    showErrors,
}: IHousingFrequency): JSX.Element {
    // ***** Redux *****
    const billsPaidFrequency = useAppSelector(selectBillsPaidFrequency);
    const dispatch = useAppDispatch();
    const fields = useFieldCopy();

    // ***** Event Handlers *****
    function handleChange(value: string | boolean | undefined): void {
        dispatch(setBillsPaidFrequency(value as EFrequency));
    }

    // ***** Render *****
    const options = [
        {
            label: 'Weekly',
            value: EFrequency.WEEKLY,
            id: `${baseId}-button-housingFrequency-weekly`,
        },
        {
            label: 'Fortnightly',
            value: EFrequency.FORTNIGHTLY,
            id: `${baseId}-button-housingFrequency-fortnightly`,
        },
        {
            label: 'Monthly',
            value: EFrequency.MONTHLY,
            id: `${baseId}-button-housingFrequency-monthly`,
        },
    ];

    return (
        <div data-testid={testId}>
            <RadioGroup
                title={fields.housingFrequency}
                showErrors={showErrors}
                onChange={handleChange}
                options={options}
                value={billsPaidFrequency}
                tooltipContent='Enter the amount you actually pay each time, and we will do the rest.'
            />
        </div>
    );
}
